import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { useContasFin } from './dados';
import { hojeLocal, invalidarContas, type Documento } from './tipos';

/**
 * Fase 2 (W14): baixa em lote dos documentos escolhidos na lista (`POST /contas/documentos/baixar-lote`, tudo ou nada).
 * Cada um pelo saldo, com juros e multa do atraso calculados pelo servidor na data; a chave de idempotência nasce com a
 * tela (reenviar por engano não baixa duas vezes). A lista já só deixa escolher um sentido por aba.
 */
export function BaixaLote({ docs, aoFechar, aoFeito }: { docs: Documento[] | null; aoFechar: () => void; aoFeito: () => void }) {
  if (!docs?.length) return null;
  return <Corpo docs={docs} aoFechar={aoFechar} aoFeito={aoFeito} />;
}

function Corpo({ docs, aoFechar, aoFeito }: { docs: Documento[]; aoFechar: () => void; aoFeito: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const contas = useContasFin();
  const [chave] = useState(() => crypto.randomUUID());
  const [data, setData] = useState(hojeLocal());
  const [contaId, setContaId] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const moeda = docs[0]!.moeda;
  const saldo = docs.reduce((a, d) => a + d.saldo_centavos, 0);
  const receber = docs[0]!.direcao === 'receber';

  const baixar = useMutation({
    mutationFn: () => post<{ total_centavos: number; baixas: unknown[] }>('/contas/documentos/baixar-lote', { documento_ids: docs.map((d) => d.id), data, conta_id: contaId || null }, { 'Idempotency-Key': chave }),
    onSuccess: async (r) => {
      await invalidarContas(qc);
      toast.sucesso(t('w14.loteFeito', { count: r.baixas.length, valor: dinheiro(r.total_centavos, moeda) }));
      aoFeito();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('w14.loteTitulo', { count: docs.length, context: docs[0]?.direcao === 'pagar' ? 'pagar' : 'receber' })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!/^\d{4}-\d{2}-\d{2}$/.test(data)} carregando={baixar.isPending} onClick={() => (setErro(null), baixar.mutate())}>
            {receber ? t('baixa.confirmarReceber') : t('baixa.confirmarPagar')}
          </Botao>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-baixa-lote>
        <p className="text-sm text-tinta-2 sm:col-span-2">{t('w14.loteTexto')}</p>
        <CampoControlado def={{ nome: 'data_lote', rotulo: t('w14.loteData'), tipo: 'data', obrigatorio: true }} valor={data} aoMudar={(v) => setData(String(v ?? ''))} />
        <CampoControlado
          def={{ nome: 'conta_lote', rotulo: t('w14.loteConta'), tipo: 'selecao', placeholder: t('w14.contaPadrao'), opcoes: (contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.padrao ? `${c.nome} (${t('baixa.padrao')})` : c.nome })) }}
          valor={contaId}
          aoMudar={(v) => setContaId(String(v ?? ''))}
        />
        <ul className="flex max-h-48 flex-col divide-y divide-borda overflow-y-auto rounded-lg border border-borda sm:col-span-2" tabIndex={0} role="region" aria-label={t('w14.loteSaldo')}>
          {docs.map((d) => (
            <li key={d.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
              <TextoCortado className="numero min-w-0">{d.numero}</TextoCortado>
              <span className="numero shrink-0">{dinheiro(d.saldo_centavos, d.moeda)}</span>
            </li>
          ))}
        </ul>
        <div className="flex min-w-0 flex-col justify-end rounded-lg border border-borda bg-sup-2 px-3 py-2 sm:col-span-2">
          <span className="kicker">{t('w14.loteSaldo')}</span>
          <TextoCortado className={`numero text-xl font-semibold ${receber ? 'text-verde' : 'text-perigo'}`}>{dinheiro(saldo, moeda)}</TextoCortado>
        </div>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-2">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
